import React, { useState, useEffect, useCallback } from 'react';
import { FiCalendar } from 'react-icons/fi';
import { FaTrophy, FaSyncAlt, FaCheckCircle, FaTimesCircle, FaDownload, FaTrash } from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import apiService from '../services/apiService';

interface MatchData {
  matchId: string;
  localTeam: string;
  visitorTeam: string;
  matchType: string;
  venue: string;
  startDate: string;
  endDate?: string;
  state: string;
  status?: string;
  scoresUpdated: boolean;
}

const Matches: React.FC = () => {
  const [series, setSeries] = useState<any[]>([]);
  const [selectedSeries, setSelectedSeries] = useState<string>('');
  const [publishFilter, setPublishFilter] = useState<string>('all');
  const [loadingSeries, setLoadingSeries] = useState(false);
  const [loadingMatches, setLoadingMatches] = useState(false);
  const [matches, setMatches] = useState<MatchData[]>([]);
  const [error, setError] = useState('');
  const [gettingMatch, setGettingMatch] = useState<{ [key: string]: boolean }>({});
  const [deletingMatch, setDeletingMatch] = useState<{ [key: string]: boolean }>({});
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    confirmText: string;
    confirmColor: string;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    confirmText: 'Confirm',
    confirmColor: 'blue',
  });
  const mainScrollRef = React.useRef<HTMLDivElement>(null);

  // Get color classes based on match state
  const getMatchStateColors = (state?: string) => {
    const stateLower = (state || '').toLowerCase();

    if (stateLower.includes('finished') || stateLower.includes('completed')) {
      return {
        bg: 'bg-gradient-to-br from-green-50 to-green-100 dark:from-green-950/40 dark:to-green-900/40',
        border: 'border-green-400 dark:border-green-600',
        hover: 'hover:border-green-500 dark:hover:border-green-500 hover:shadow-green-100 dark:hover:shadow-green-900/20',
        badge: 'bg-green-500 text-white',
      };
    }
    if (stateLower.includes('live') || stateLower.includes('in progress')) {
      return {
        bg: 'bg-gradient-to-br from-rose-50 to-red-50 dark:from-rose-950/40 dark:to-red-950/40',
        border: 'border-rose-500 dark:border-rose-600',
        hover: 'hover:border-rose-600 dark:hover:border-rose-500 hover:shadow-rose-100 dark:hover:shadow-rose-900/20',
        badge: 'bg-rose-500 text-white animate-pulse',
      };
    }
    if (stateLower.includes('upcoming') || stateLower.includes('scheduled')) {
      return {
        bg: 'bg-gradient-to-br from-sky-50 to-blue-50 dark:from-sky-950/40 dark:to-blue-950/40',
        border: 'border-sky-400 dark:border-sky-600',
        hover: 'hover:border-sky-500 dark:hover:border-sky-500 hover:shadow-sky-100 dark:hover:shadow-sky-900/20',
        badge: 'bg-sky-500 text-white',
      };
    }
    if (stateLower.includes('cancelled') || stateLower.includes('abandoned') || stateLower.includes('aban')) {
      return {
        bg: 'bg-gradient-to-br from-gray-50 to-slate-50 dark:from-gray-900/40 dark:to-slate-900/40',
        border: 'border-gray-400 dark:border-gray-600',
        hover: 'hover:border-gray-500 dark:hover:border-gray-500',
        badge: 'bg-gray-500 text-white',
      };
    }
    if (stateLower.includes('delayed') || stateLower.includes('postponed')) {
      return {
        bg: 'bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-950/40 dark:to-amber-950/40',
        border: 'border-orange-400 dark:border-orange-600',
        hover: 'hover:border-orange-500 dark:hover:border-orange-500',
        badge: 'bg-orange-500 text-white',
      };
    }

    return {
      bg: 'bg-gradient-to-br from-violet-50 to-purple-50 dark:from-violet-950/40 dark:to-purple-950/40',
      border: 'border-violet-400 dark:border-violet-600',
      hover: 'hover:border-violet-500 dark:hover:border-violet-500 hover:shadow-violet-100 dark:hover:shadow-violet-900/20',
      badge: 'bg-violet-500 text-white',
    };
  };

  // Load series
  const loadSeries = useCallback(async () => {
    try {
      setLoadingSeries(true);
      setError('');
      const response = await apiService.getSeries();
      const apiSeries = response?.data || response || [];

      // Map to consistent structure with publish status
      const formattedSeries = apiSeries.map((s: any) => ({
        seriesId: s.seriesId || s.id,
        name: s.name || s.title,
        publish: s.publish || false
      }));

      setSeries(formattedSeries);
    } catch (err) {
      console.error('Error loading series:', err);
      setError('Failed to load series');
    } finally {
      setLoadingSeries(false);
    }
  }, []);

  // Filter series based on publish status
  const filteredSeries = series.filter((s) => {
    if (publishFilter === 'published') return s.publish === true;
    if (publishFilter === 'unpublished') return s.publish === false;
    return true; // 'all'
  });

  useEffect(() => {
    loadSeries();
  }, [loadSeries]);

  const loadMatches = useCallback(async () => {
    if (!selectedSeries) return;

    try {
      setLoadingMatches(true);
      setError('');

      // Get series details by ID which includes match info
      const foundSeries = await apiService.getSeriesById(selectedSeries) as any;

      if (foundSeries && foundSeries.matchInfo) {
        const matchesData: MatchData[] = foundSeries.matchInfo
          .sort((a: any, b: any) => {
            const dateA = a.startDate ? new Date(a.startDate).getTime() : 0;
            const dateB = b.startDate ? new Date(b.startDate).getTime() : 0;
            return dateA - dateB;
          })
          .map((match: any) => ({
            matchId: String(match.matchId),
            localTeam: match.localTeam?.teamSName || match.localTeam?.name || match.localTeam?.teamName || 'Team 1',
            visitorTeam: match.visitorTeam?.teamSName || match.visitorTeam?.name || match.visitorTeam?.teamName || 'Team 2',
            matchType: match.matchType || match.matchFormat || 'N/A',
            venue: match.venueInfo?.ground || match.venue || 'N/A',
            startDate: match.startDate,
            endDate: match.endDate,
            state: match.state || 'Unknown',
            status: match.status,
            scoresUpdated: match.scoresUpdated || false,
          }));

        setMatches(matchesData);
      } else {
        setMatches([]);
      }
    } catch (err) {
      console.error('Error loading matches:', err);
      setError('Failed to load matches');
      setMatches([]);
    } finally {
      setLoadingMatches(false);
    }
  }, [selectedSeries]);

  // Load matches when series is selected
  useEffect(() => {
    if (selectedSeries) {
      loadMatches();
    }
  }, [selectedSeries, loadMatches]);

  // Get match details
  const handleGetMatchClick = (matchId: string, localTeam: string, visitorTeam: string) => {
    setConfirmModal({
      isOpen: true,
      title: 'Fetch Match Details',
      message: `Do you want to fetch match details for:\n${localTeam} vs ${visitorTeam} (Match #${matchId})?`,
      confirmText: 'Fetch',
      confirmColor: 'emerald',
      onConfirm: () => {
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
        performGetMatch(matchId);
      },
    });
  };

  const performGetMatch = async (matchId: string) => {
    if (!selectedSeries) return;

    try {
      setGettingMatch(prev => ({ ...prev, [matchId]: true }));
      const matchData = await apiService.getSeriesMatch(selectedSeries, matchId);
      console.log('Match data:', matchData);
      toast.success(`Match #${matchId} details fetched successfully!`);
      await loadMatches(); // Refresh matches
    } catch (err: any) {
      console.error(`Error fetching match ${matchId}:`, err);
      toast.error(err.message || `Failed to fetch Match #${matchId}`);
    } finally {
      setGettingMatch(prev => ({ ...prev, [matchId]: false }));
    }
  };

  // Remove match
  const handleDeleteMatchClick = (matchId: string, localTeam: string, visitorTeam: string) => {
    setConfirmModal({
      isOpen: true,
      title: 'Remove Match',
      message: `Are you sure you want to remove:\n${localTeam} vs ${visitorTeam} (Match #${matchId})?\n\nThis action cannot be undone.`,
      confirmText: 'Remove',
      confirmColor: 'red',
      onConfirm: () => {
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
        performDeleteMatch(matchId);
      },
    });
  };

  const performDeleteMatch = async (matchId: string) => {
    if (!selectedSeries) return;

    try {
      setDeletingMatch(prev => ({ ...prev, [matchId]: true }));
      await apiService.deleteSeriesMatch(selectedSeries, matchId);
      toast.success(`Match #${matchId} removed successfully!`);
      await loadMatches(); // Refresh matches
    } catch (err: any) {
      console.error(`Error removing match ${matchId}:`, err);
      toast.error(err.message || `Failed to remove Match #${matchId}`);
    } finally {
      setDeletingMatch(prev => ({ ...prev, [matchId]: false }));
    }
  };

  // Auto-scroll to first upcoming or last completed match
  useEffect(() => {
    if (matches.length > 0 && mainScrollRef.current) {
      const timer = setTimeout(() => {
        const container = mainScrollRef.current;
        if (!container) return;

        const matchCards = container.querySelectorAll('[data-match-card]');
        let targetCard: HTMLElement | null = null;
        let lastCompletedCard: HTMLElement | null = null;

        matchCards.forEach((card) => {
          const matchState = card.getAttribute('data-match-state')?.toLowerCase() || '';

          if (!targetCard && (matchState.includes('upcoming') || matchState.includes('scheduled'))) {
            targetCard = card as HTMLElement;
          }

          if (matchState.includes('finished') || matchState.includes('completed')) {
            lastCompletedCard = card as HTMLElement;
          }
        });

        const cardToScrollTo: HTMLElement | null = targetCard || lastCompletedCard;

        if (cardToScrollTo) {
          const containerWidth = container.offsetWidth;
          const cardLeft = (cardToScrollTo as HTMLElement).offsetLeft;
          const cardWidth = (cardToScrollTo as HTMLElement).offsetWidth;
          const targetScrollLeft = cardLeft - (containerWidth / 2) + (cardWidth / 2);

          container.scrollLeft = targetScrollLeft;
        }
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [matches]);

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center space-x-3">
          <div className="p-3 bg-blue-100 dark:bg-primary-500/20 rounded-lg border border-blue-200 dark:border-primary-500/30">
            <FaTrophy className="h-6 w-6 text-blue-600 dark:text-primary-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Matches</h1>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              {selectedSeries ? `Viewing ${matches.length} matches` : 'Select a series to view matches'}
            </p>
          </div>
        </div>
        {selectedSeries && (
          <button
            onClick={loadMatches}
            disabled={loadingMatches}
            className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 bg-blue-600 dark:bg-primary-600 text-white rounded-lg font-medium hover:bg-blue-700 dark:hover:bg-primary-700 transition-colors shadow-sm disabled:opacity-50"
          >
            <FaSyncAlt className={`h-4 w-4 mr-2 ${loadingMatches ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        )}
      </div>

      {/* Series Selector */}
      <div className="bg-white dark:bg-dark-400/60 backdrop-blur-sm rounded-lg dark:rounded-xl border border-gray-200 dark:border-dark-300/40 shadow-sm dark:shadow-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Publish Filter */}
          <div>
            <label htmlFor="publish-filter" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Publish Status
            </label>
            <select
              id="publish-filter"
              value={publishFilter}
              onChange={(e) => {
                setPublishFilter(e.target.value);
                setSelectedSeries(''); // Reset series selection when filter changes
              }}
              className="block w-full px-3 py-2 border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-primary-500 focus:border-transparent"
            >
              <option value="all">All Series</option>
              <option value="published">Published Only</option>
              <option value="unpublished">Unpublished Only</option>
            </select>
          </div>

          {/* Series Selector */}
          <div>
            <label htmlFor="series-select" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <FiCalendar className="inline h-4 w-4 mr-1" />
              Select Series
            </label>
            <select
              id="series-select"
              value={selectedSeries}
              onChange={(e) => setSelectedSeries(e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">-- Select a series --</option>
              {loadingSeries ? (
                <option disabled>Loading...</option>
              ) : (
                filteredSeries.map((s) => (
                  <option key={s.seriesId} value={String(s.seriesId)}>
                    {s.name} {s.publish ? '✓' : ''}
                  </option>
                ))
              )}
            </select>
          </div>
        </div>

      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 dark:bg-danger-500/20 border border-red-200 dark:border-danger-500/30 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <FaTimesCircle className="h-5 w-5 text-red-600 dark:text-danger-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-red-800 dark:text-danger-500">{error}</div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loadingMatches && (
        <div className="bg-white dark:bg-dark-400/60 backdrop-blur-sm rounded-lg dark:rounded-xl border border-gray-200 dark:border-dark-300/40 shadow-sm dark:shadow-lg">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-primary-500 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Loading matches...</p>
          </div>
        </div>
      )}

      {/* Matches Display - Horizontal Scroll */}
      {!loadingMatches && selectedSeries && matches.length > 0 && (
        <div className="bg-white dark:bg-dark-400/60 backdrop-blur-sm rounded-lg border border-gray-200 dark:border-dark-300/40 shadow-sm dark:shadow-lg overflow-hidden">
          <div ref={mainScrollRef} className="overflow-x-auto p-4">
            <div className="flex gap-3">
              {matches.map((match) => {
                const colors = getMatchStateColors(match.state);
                return (
                  <div
                    key={match.matchId}
                    data-match-card
                    data-match-state={match.state}
                    className={`flex-shrink-0 w-64 ${colors.bg} rounded-xl border-2 ${colors.border} p-3 ${colors.hover} transition-all hover:shadow-lg hover:scale-[1.02]`}
                  >
                    {/* Match ID and State Badge */}
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm font-mono font-bold text-gray-800 dark:text-gray-100">
                        #{match.matchId}
                      </div>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${colors.badge} font-semibold shadow-sm`}>
                        {match.state}
                      </span>
                    </div>

                    {/* Teams */}
                    <div className="mb-3">
                      <div className="flex items-center justify-center gap-1.5">
                        <span className="truncate max-w-[100px] bg-white/60 dark:bg-black/20 px-2 py-1 rounded text-xs font-semibold text-gray-700 dark:text-gray-200" title={match.localTeam}>
                          {match.localTeam}
                        </span>
                        <span className="text-[10px] font-bold text-gray-500 dark:text-gray-400">vs</span>
                        <span className="truncate max-w-[100px] bg-white/60 dark:bg-black/20 px-2 py-1 rounded text-xs font-semibold text-gray-700 dark:text-gray-200" title={match.visitorTeam}>
                          {match.visitorTeam}
                        </span>
                      </div>
                    </div>

                    {/* Match Details */}
                    <div className="space-y-1.5 mb-3">
                      <div className="flex items-center justify-between text-[11px]">
                        <span className="text-gray-600 dark:text-gray-400">Type:</span>
                        <span className="font-semibold text-gray-800 dark:text-gray-200 bg-white/40 dark:bg-black/20 px-1.5 py-0.5 rounded">
                          {match.matchType}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-[11px]">
                        <span className="text-gray-600 dark:text-gray-400">Venue:</span>
                        <span className="font-medium text-gray-800 dark:text-gray-200 text-right truncate max-w-[140px]" title={match.venue}>
                          {match.venue}
                        </span>
                      </div>
                      {match.startDate && (
                        <div className="flex items-center justify-between text-[11px]">
                          <span className="text-gray-600 dark:text-gray-400">Date:</span>
                          <span className="font-medium text-gray-800 dark:text-gray-200">
                            {new Date(match.startDate).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                            })}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Status */}
                    {match.status && (
                      <div className="mb-2 text-[10px] text-center p-1.5 bg-white/50 dark:bg-black/20 rounded">
                        <p className="text-gray-700 dark:text-gray-300 line-clamp-2">{match.status}</p>
                      </div>
                    )}

                    {/* Scores Updated Badge */}
                    <div className="mb-2 flex justify-center">
                      <div className={`flex items-center gap-1 px-2.5 py-1 rounded-md font-semibold text-[11px] ${
                        match.scoresUpdated
                          ? 'bg-green-500 text-white'
                          : 'bg-orange-500 text-white'
                      }`}>
                        {match.scoresUpdated ? (
                          <FaCheckCircle className="h-3 w-3" />
                        ) : (
                          <FaTimesCircle className="h-3 w-3" />
                        )}
                        <span>{match.scoresUpdated ? 'Updated' : 'Pending'}</span>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="grid grid-cols-2 gap-1.5">
                      {/* Get Match Button - Only enabled if scores not updated */}
                      <button
                        onClick={() => handleGetMatchClick(match.matchId, match.localTeam, match.visitorTeam)}
                        disabled={match.scoresUpdated || gettingMatch[match.matchId] || deletingMatch[match.matchId]}
                        className="flex items-center justify-center gap-1 px-2 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title={match.scoresUpdated ? 'Match already updated' : 'Fetch match details'}
                      >
                        {gettingMatch[match.matchId] ? (
                          <>
                            <div className="animate-spin rounded-full h-2.5 w-2.5 border border-white border-t-transparent"></div>
                            <span>Loading...</span>
                          </>
                        ) : (
                          <>
                            <FaDownload className="h-2.5 w-2.5" />
                            <span>Get</span>
                          </>
                        )}
                      </button>

                      {/* Remove Match Button - Only enabled if scores are updated */}
                      <button
                        onClick={() => handleDeleteMatchClick(match.matchId, match.localTeam, match.visitorTeam)}
                        disabled={!match.scoresUpdated || gettingMatch[match.matchId] || deletingMatch[match.matchId]}
                        className="flex items-center justify-center gap-1 px-2 py-1.5 rounded-md bg-red-600 hover:bg-red-700 text-white text-[10px] font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title={!match.scoresUpdated ? 'Update match first' : 'Remove match'}
                      >
                        {deletingMatch[match.matchId] ? (
                          <>
                            <div className="animate-spin rounded-full h-2.5 w-2.5 border border-white border-t-transparent"></div>
                            <span>Removing...</span>
                          </>
                        ) : (
                          <>
                            <FaTrash className="h-2.5 w-2.5" />
                            <span>Remove</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Empty State - When series selected but no matches */}
      {!loadingMatches && selectedSeries && matches.length === 0 && !error && (
        <div className="bg-white dark:bg-dark-400/60 backdrop-blur-sm rounded-lg dark:rounded-xl border border-gray-200 dark:border-dark-300/40 shadow-sm dark:shadow-lg">
          <div className="text-center py-12">
            <div className="mx-auto h-16 w-16 bg-gray-100 dark:bg-dark-300/40 rounded-full flex items-center justify-center mb-4">
              <FaTrophy className="h-8 w-8 text-gray-400 dark:text-gray-500" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No Matches Found</h3>
            <p className="text-gray-600 dark:text-gray-400">No matches found for this series</p>
          </div>
        </div>
      )}

      {/* No Series Selected State */}
      {!selectedSeries && (
        <div className="bg-white dark:bg-dark-400/60 backdrop-blur-sm rounded-lg dark:rounded-xl border border-gray-200 dark:border-dark-300/40 shadow-sm dark:shadow-lg">
          <div className="text-center py-12">
            <div className="mx-auto h-16 w-16 bg-blue-100 dark:bg-primary-500/20 rounded-full flex items-center justify-center mb-4 border border-blue-200 dark:border-primary-500/30">
              <FiCalendar className="h-8 w-8 text-blue-500 dark:text-primary-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Select a series</h3>
            <p className="text-gray-600 dark:text-gray-400">Choose a series from the dropdown to view matches</p>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {confirmModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-dark-400 rounded-xl shadow-2xl max-w-md w-full">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 dark:border-dark-300">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {confirmModal.title}
              </h3>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-4">
              <p className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-line">
                {confirmModal.message}
              </p>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 bg-gray-50 dark:bg-dark-500 rounded-b-xl flex justify-end gap-3">
              <button
                onClick={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-dark-400 border border-gray-300 dark:border-dark-300 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmModal.onConfirm}
                className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${
                  confirmModal.confirmColor === 'red'
                    ? 'bg-red-600 hover:bg-red-700'
                    : confirmModal.confirmColor === 'emerald'
                    ? 'bg-emerald-600 hover:bg-emerald-700'
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {confirmModal.confirmText}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Container */}
      <ToastContainer
        position="bottom-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
};

export default Matches;
